import {
  IGridFilterDefinition,
  IGridFilter,
  IDateGridFilter,
  ITextGridFilter,
  IEnumGridFilter,
  IArrayGridFilter,
  IDateRange,
  GridFilterPredefinedDateRange,
  IArrayGridFilterDefinition,
  GridFilterEntityDataItem,
  GridFilterValue,
  toGridDefinitionMap,
  GridFilterDefinitions,
  createFilter,
  IEntityArrayGridFilterDefinition,
  expandFilterValue,
  ArrayFilterType,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { IParameter } from "@gtmhub/parameters/models";
import { unique } from "@gtmhub/util";
import { IInsightboardItemSetting } from "./models/models";
import { localize } from "@gtmhub/localization";

type GridFilterDefinitionFactory = (parameter: IParameter) => IGridFilterDefinition;

type InsightboardItemSettingToGridFilterValueConverter = (parameter: IParameter, value: string) => GridFilterValue;

type GridFilterToInsightboardItemSettingValueConverter = (filter: IGridFilter) => IInsightboardItemSetting;

type FactoryPair = [GridFilterDefinitionFactory, GridFilterToInsightboardItemSettingValueConverter, InsightboardItemSettingToGridFilterValueConverter];

const isEntityBound = (p: IParameter) => p.entityName && p.entityTitleField && p.entityValueField;

export const toGridFilterEntityDataItems = (item: IInsightboardItemSetting): GridFilterEntityDataItem[] => {
  if (!item.value) {
    return [];
  }

  const values = item.value.split(",").filter((x) => !!x);
  const displayNames = item.valueDisplayName ? item.valueDisplayName.split(",") : [];
  if (displayNames.length === values.length) {
    return values.map<GridFilterEntityDataItem>((value, i) => ({ displayName: displayNames[i], value }));
  }

  return values.map<GridFilterEntityDataItem>((value) => ({ displayName: value, value }));
};

const createEntityArrayDefinition = (p: IParameter, override: Partial<IEntityArrayGridFilterDefinition> = {}): IArrayGridFilterDefinition => ({
  dataType: "array",
  arrayType: "entity",
  entity: { name: p.entityName, displayNameField: p.entityTitleField, valueField: p.entityValueField },
  defaultOperator: "isOneOf",
  supportedOperators: ["isOneOf"],
  displayName: p.title,
  trackingName: p.title,
  fieldName: p.key,
  ...override,
});

const createArrayDefinition = (p: IParameter): IArrayGridFilterDefinition => {
  let arrayType;
  if (p.entityValueField === "id" && entityNameGridFilterArrayTypeMap.has(p.entityName)) {
    arrayType = entityNameGridFilterArrayTypeMap.get(p.entityName);
    return {
      dataType: "array",
      arrayType: arrayType,
      defaultOperator: "isOneOf",
      supportedOperators: ["isOneOf"],
      displayName: p.title,
      trackingName: p.title,
      fieldName: p.key,
    };
  } else {
    return createEntityArrayDefinition(p);
  }
};

const entityNameGridFilterArrayTypeMap: Map<string, ArrayFilterType> = new Map([
  ["gtmhubassignees", "assignee"],
  ["gtmhubteams", "team"],
  ["gtmhubusers", "user"],
  ["gtmhubplanningsessions", "session"],
]);

export const createInsightboardSettingFromGridFilter = (filter: IGridFilter): IInsightboardItemSetting => {
  switch (filter.dataType) {
    case "array":
      return createInsightboardItemSettingFromArrayFilter(filter);
    case "text":
      return createInsightboardItemSettingFromTextGridFilter(filter);
    case "enum":
      return createInsightboardItemSettingFromEnumFilter(filter);
    case "date":
      return createInsightboardItemSettingFromDateGridFilter(filter);
    default:
      console.warn("unsupported converstion from grid filter to insightboard item setting");
  }
};

const createInsightboardItemSettingFromEnumOrArrayFilter = (filter: IEnumGridFilter | IArrayGridFilter): IInsightboardItemSetting => {
  if (filter.dataType === "array") {
    return createInsightboardItemSettingFromArrayFilter(filter);
  }

  return createInsightboardItemSettingFromEnumFilter(filter);
};

const createInsightboardItemSettingFromArrayFilter = (filter: IArrayGridFilter): IInsightboardItemSetting => ({
  name: filter.fieldName,
  value: expandFilterValue(filter).toString(),
  valueDisplayName: filter.entityData ? filter.entityData.map((x) => x.displayName).toString() : "",
});

const createInsightboardItemSettingFromEnumFilter = (filter: IEnumGridFilter): IInsightboardItemSetting => ({
  name: filter.fieldName,
  value: (filter.value || []).toString(),
  valueDisplayName: "",
});

const createInsightboardItemSettingFromDateGridFilter = (filter: IDateGridFilter): IInsightboardItemSetting => ({
  name: filter.fieldName,
  value: filter.value ? (typeof filter.value === "string" ? filter.value : `${filter.value.startDate}|${filter.value.endDate}`) : "",
  valueDisplayName: "",
});

const createInsightboardItemSettingFromTextGridFilter = (filter: ITextGridFilter): IInsightboardItemSetting => ({
  name: filter.fieldName,
  value: filter.value,
  valueDisplayName: "",
});

const dateRangeFactory: FactoryPair = [
  (p: IParameter): IGridFilterDefinition => ({
    dataType: "date",
    predefinedDateRanges: p.selectorType === "systemdaterange" ? p.predefinedValues.map<GridFilterPredefinedDateRange>((x) => ({ displayName: x, value: x })) : undefined,
    fieldName: p.key,
    displayName: p.title,
    trackingName: p.title,
    defaultOperator: "isBetween",
    supportedOperators: ["isBetween"],
    local: true,
  }),
  (filter: IDateGridFilter): IInsightboardItemSetting => createInsightboardItemSettingFromDateGridFilter(filter),
  (p: IParameter, value: string): string | IDateRange => {
    if (value && value.indexOf("|") > 0) {
      const [startDate, endDate] = value.split("|");
      return { startDate, endDate };
    }
    return value;
  },
];

/**
 * Record key is insigitboard parameter selectorType and the FactoryPair includes 3 conversion methods:
 * 1. insightboard parameter -> grid filter definition
 * 2. grid filter -> insightboard setting
 * 3. insightboard parameter and value -> grid filter value
 */
export const filterFactory: Record<string, FactoryPair> = {
  daterange: dateRangeFactory,
  systemdaterange: dateRangeFactory,
  datepicker: [
    (p: IParameter): IGridFilterDefinition => ({
      dataType: "date",
      fieldName: p.key,
      displayName: p.title,
      trackingName: p.title,
      defaultOperator: "equals",
      supportedOperators: ["equals"],
    }),
    (filter: ITextGridFilter): IInsightboardItemSetting => createInsightboardItemSettingFromTextGridFilter(filter),
    (p: IParameter, value: string): string => value,
  ],
  text: [
    (p: IParameter): IGridFilterDefinition => ({
      dataType: "text",
      fieldName: p.key,
      displayName: p.title,
      trackingName: p.title,
      defaultOperator: "equals",
      supportedOperators: ["equals"],
    }),
    (filter: ITextGridFilter): IInsightboardItemSetting => createInsightboardItemSettingFromTextGridFilter(filter),
    (p: IParameter, value: string): string => value,
  ],
  list: [
    (p: IParameter): IGridFilterDefinition =>
      isEntityBound(p)
        ? createEntityArrayDefinition(p, { maxValues: 1 })
        : {
            dataType: "enum",
            entries: p.predefinedValues || [],
            fieldName: p.key,
            displayName: p.title,
            trackingName: p.title,
            defaultOperator: "equals",
            supportedOperators: ["equals"],
          },
    (filter: IEnumGridFilter | IArrayGridFilter): IInsightboardItemSetting => createInsightboardItemSettingFromEnumOrArrayFilter(filter),
    (p: IParameter, value: string): string | string[] => (isEntityBound(p) && !!value ? [value] : value),
  ],
  multiselect: [
    (p: IParameter): IGridFilterDefinition =>
      p.key === "team" || p.key === "session"
        ? {
            dataType: "array",
            arrayType: p.key,
            fieldName: p.key,
            displayName: p.title,
            trackingName: p.title,
            defaultOperator: "isOneOf",
            supportedOperators: ["isOneOf"],
          }
        : isEntityBound(p)
        ? createArrayDefinition(p)
        : {
            dataType: "array",
            arrayType: "static",
            entries: p.predefinedValues || [],
            fieldName: p.key,
            displayName: p.title,
            trackingName: p.title,
            defaultOperator: "isOneOf",
            supportedOperators: ["isOneOf"],
          },
    (filter: IEnumGridFilter | IArrayGridFilter): IInsightboardItemSetting => createInsightboardItemSettingFromEnumOrArrayFilter(filter),
    (p: IParameter, value: string): string[] => (value ? unique(value.split(",")) : []),
  ],
};

export const insightboardsFilterDefinitions = (): GridFilterDefinitions => {
  return toGridDefinitionMap([
    {
      fieldName: "tags.title",
      displayName: localize("tags"),
      trackingName: "Tags",
      dataType: "array",
      arrayType: "tag",
      defaultOperator: "contains",
      supportedOperators: ["contains"],
    },
    {
      fieldName: "createdById",
      displayName: localize("created_by_cap"),
      trackingName: "Created by",
      dataType: "array",
      arrayType: "user",
      defaultOperator: "isOneOf",
      supportedOperators: ["isOneOf"],
    },
    {
      fieldName: "dateCreated",
      displayName: localize("date_created_cap"),
      trackingName: "Date created",
      dataType: "date",
      defaultOperator: "isBefore",
      supportedOperators: ["isBefore", "isAfter", "isBetween", "isNotBetween"],
    },
    {
      fieldName: "dateModified",
      displayName: localize("last_updated"),
      trackingName: "Last updated",
      dataType: "date",
      defaultOperator: "isBefore",
      supportedOperators: ["isBefore", "isAfter", "isBetween", "isNotBetween"],
    },
  ]);
};

export const generateInsightboardsFilters = (filterDefinitions: GridFilterDefinitions, isTagsModuleEnabled: boolean): IGridFilter[] => {
  if (!isTagsModuleEnabled) {
    return [createFilter(filterDefinitions["createdById"]), createFilter(filterDefinitions["dateCreated"])];
  }
  return [createFilter(filterDefinitions["tags.title"]), createFilter(filterDefinitions["createdById"]), createFilter(filterDefinitions["dateCreated"])];
};
